import React, { useEffect } from 'react'
import { TableRow, TableCell } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { CvsTable, PendingStatusIndicator, TableCellTooltip } from '@components'
import { Submission } from '@global/types'
import { getCellContent } from 'functions'
import { COLUMNS, getColumns, LIMIT_PER_PAGE } from '../../config'
import { useApprovalContext } from '../../contexts'
import { ActionType, Order, OrderBy } from '../../contexts/approvalContext/types'
import { EmptyState } from '../EmptyState'
import { Layout } from '../Layout'
import { ReviewButton } from '../ReviewButton'
import * as css from './styles'

export const ApprovalRequestsTable = () => {
  const location = useLocation()
  const [DATASETS_PAGE_ENABLED = false] = useSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )
  const {
    approvalRequests,
    isLoading,
    reloadApprovalRequests,
    requestsBySubmitter,
    requestsWithinDateRange,
    sortOptions,
    dispatch,
    updateApprovalRequests,
  } = useApprovalContext() ?? {
    approvalRequests: [],
    isLoading: false,
    reloadApprovalRequests: false,
    requestsBySubmitter: [],
    requestsWithinDateRange: [],
    sortOptions: {
      order: 'desc',
      orderBy: 'created_at',
      offset: 0,
    },
    dispatch: () => void {},
    updateApprovalRequests: () => void {},
  }

  useEffect(() => {
    if (!reloadApprovalRequests) return
    void updateApprovalRequests({ ...sortOptions })
  }, [reloadApprovalRequests])

  const addTemplateNameColumn = location.pathname.includes('/codes/')
  const noRequests = Boolean(!approvalRequests?.length)
  const noRequestsWithinDateRange = requestsWithinDateRange.length === 0
  const noResults = noRequests || noRequestsWithinDateRange

  if (isLoading) {
    return (
      <Layout>
        <css.LoadingContainer>
          <CircularProgress color="primary" />
        </css.LoadingContainer>
      </Layout>
    )
  }

  if (noResults) {
    return (
      <Layout>
        <EmptyState />
      </Layout>
    )
  }

  const rows = requestsBySubmitter ?? requestsWithinDateRange
  const columns = addTemplateNameColumn ? getColumns(DATASETS_PAGE_ENABLED) : COLUMNS

  return (
    <Layout>
      <css.TableContainer>
        <CvsTable
          rows={rows as never[]}
          columns={columns}
          orderBy={sortOptions.orderBy}
          sortOrder={sortOptions.order}
          noAdd
          noPagination
          customHandleSort={rows.length === LIMIT_PER_PAGE ? handleSort : null}
          persistSortOptions={persistSortOptions}
          headerRowPadding={50}
        >
          {({ row }: { row: Submission }) => {
            const numberOfRows = getCellContent('number', row.rows) as React.ReactElement // TODO: remove when import file is converted to TS
            const submittedDate = getCellContent('date', row.created_at) as React.ReactElement // TODO: remove when import file is converted to TS

            return (
              <TableRow css={css.tableRow}>
                {addTemplateNameColumn && (
                  <TableCellTooltip title={row.template_name}>
                    <TableCell
                      css={css.tableCell}
                      style={{ paddingLeft: 50 }}
                      component="td"
                      scope="row"
                      padding="normal"
                    >
                      {row.template_name}
                    </TableCell>
                  </TableCellTooltip>
                )}
                <TableCellTooltip title={row.submission_name}>
                  <TableCell
                    css={css.tableCell}
                    style={{ paddingLeft: addTemplateNameColumn ? 16 : 50 }}
                    component="td"
                    scope="row"
                    padding="normal"
                  >
                    {row.submission_name}
                  </TableCell>
                </TableCellTooltip>
                <TableCellTooltip title={row.created_by}>
                  <TableCell css={css.tableCell} component="td" scope="row" padding="normal">
                    {row.created_by}
                  </TableCell>
                </TableCellTooltip>
                <TableCellTooltip title={numberOfRows}>
                  <TableCell css={css.tableCell} component="td" scope="row" padding="normal">
                    {numberOfRows}
                  </TableCell>
                </TableCellTooltip>
                <TableCellTooltip title={submittedDate}>
                  <TableCell css={css.tableCell} component="td" scope="row" padding="normal">
                    {submittedDate}
                  </TableCell>
                </TableCellTooltip>
                <TableCell css={css.tableCell} component="td" scope="row" padding="normal">
                  <div css={css.statusContainer}>
                    <PendingStatusIndicator status={row.status} />
                  </div>
                </TableCell>
                <TableCell component="td" scope="row" padding="normal">
                  <ReviewButton approvalRequest={row} />
                </TableCell>
              </TableRow>
            )
          }}
        </CvsTable>
      </css.TableContainer>
    </Layout>
  )

  /**
   * Custom function that overrides the default sort functionality of CvsTable.
   *
   * The endpoint that retrieves submissions handles sorting and returns 100 records.
   * To correctly sort through all the submissions if there are more than 100,
   * we need to re-fetch submissions with the updated sort options.
   *
   * @param {OrderBy} orderBy - The lookup value of column to sort by
   */
  function handleSort(orderBy: OrderBy) {
    const order = sortOptions.order === Order.DESC ? Order.ASC : Order.DESC

    void updateApprovalRequests({
      ...sortOptions,
      orderBy,
      order,
    })
  }

  /**
   * Without this, the selected column to sort by is reset to the initial value
   * every time a filter or page is changed.
   *
   * @param {OrderBy} orderBy - The lookup value of the column to sort by
   * @param {Order} order - The order to sort by (asc, desc)
   */
  function persistSortOptions(orderBy: OrderBy, order: Order) {
    dispatch({
      type: ActionType.setSortOptions,
      payload: {
        ...sortOptions,
        orderBy,
        order,
      },
    })
  }
}
